import { useState } from "react";
import { MessageSquarePlus, X, Bug, Lightbulb, MessageCircle, Check } from "lucide-react";
import { feedbackApi } from "../lib/api";
import { useI18n } from "../lib/i18n";

type Cat = "bug" | "idea" | "other";
const CATS: { value: Cat; icon: typeof Bug; key: string }[] = [
  { value: "bug", icon: Bug, key: "feedback.category.bug" },
  { value: "idea", icon: Lightbulb, key: "feedback.category.idea" },
  { value: "other", icon: MessageCircle, key: "feedback.category.other" },
];

/**
 * Floating feedback launcher — pinned to the bottom inline-end corner on every
 * authenticated page (mounted in Shell). Opens a small modal with a
 * category + message, POSTs to /api/feedback, and shows a thank-you state.
 * RTL-safe: uses logical `end-*` insets so it mirrors in fa/ar.
 */
export default function FeedbackButton() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState<Cat>("idea");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

  function reset() {
    setMessage("");
    setCat("idea");
    setState("idle");
  }

  function close() {
    setOpen(false);
    // let the close animation settle before clearing
    setTimeout(reset, 200);
  }

  async function submit() {
    if (message.trim().length < 3) {
      setState("error");
      return;
    }
    setState("sending");
    try {
      await feedbackApi.submit({
        category: cat,
        message: message.trim(),
        url: window.location.pathname,
        user_agent: navigator.userAgent,
      });
      setState("done");
    } catch {
      setState("error");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-gold fixed bottom-4 end-4 z-40 shadow-lg !rounded-full !px-4 !py-3"
        title={t("feedback.button")}
        aria-label={t("feedback.button")}
      >
        <MessageSquarePlus className="w-5 h-5" />
        <span className="hidden sm:inline">{t("feedback.button")}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-end sm:place-items-center p-4"
          style={{ backgroundColor: "rgb(var(--shadow) / 0.5)", backdropFilter: "blur(4px)" }}
          onClick={close}
        >
          <div
            className="w-full sm:max-w-md rounded-2xl p-5 shadow-2xl"
            style={{
              backgroundColor: "rgb(var(--bg-800))",
              border: "1px solid rgb(var(--border-mid))",
              boxShadow: "var(--shadow-glass-hover)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-parchment">{t("feedback.title")}</h2>
                <p className="text-sm text-parchment-muted mt-0.5">{t("feedback.subtitle")}</p>
              </div>
              <button
                type="button"
                onClick={close}
                className="text-parchment-muted hover:text-parchment p-1 -m-1"
                aria-label={t("common.close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {state === "done" ? (
              <div className="py-8 text-center">
                <div
                  className="mx-auto mb-3 grid place-items-center w-12 h-12 rounded-full"
                  style={{ backgroundColor: "rgb(var(--glow) / 0.2)" }}
                >
                  <Check className="w-6 h-6 text-gold-500" />
                </div>
                <p className="text-parchment font-semibold">{t("feedback.thanks")}</p>
                <button type="button" onClick={close} className="btn-ghost mt-4 mx-auto">
                  {t("common.close")}
                </button>
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-parchment-muted mb-2">
                    {t("feedback.category")}
                  </p>
                  <div className="flex gap-2">
                    {CATS.map(({ value, icon: Icon, key }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setCat(value)}
                        className={
                          "flex-1 flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold border transition " +
                          (cat === value
                            ? "border-transparent text-ink"
                            : "text-parchment-muted hover:text-parchment")
                        }
                        style={
                          cat === value
                            ? { backgroundColor: "rgb(var(--glow))", color: "#1a1206" }
                            : { borderColor: "rgb(var(--border-mid))", backgroundColor: "rgb(var(--surface-1))" }
                        }
                      >
                        <Icon className="w-4 h-4" />
                        {t(key)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs font-semibold uppercase tracking-wider text-parchment-muted">
                    {t("feedback.message")}
                  </label>
                  <textarea
                    className="input mt-2 w-full min-h-[120px] resize-y"
                    placeholder={t("feedback.placeholder")}
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      if (state === "error") setState("idle");
                    }}
                    autoFocus
                  />
                  {state === "error" && (
                    <p className="text-sm mt-1" style={{ color: "rgb(var(--pnl-neg))" }}>
                      {message.trim().length < 3 ? t("feedback.tooShort") : t("feedback.error")}
                    </p>
                  )}
                </div>

                <div className="mt-5 flex justify-end gap-2">
                  <button type="button" onClick={close} className="btn-ghost">
                    {t("common.cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={submit}
                    disabled={state === "sending"}
                    className="btn-gold"
                  >
                    {state === "sending" ? t("feedback.sending") : t("feedback.send")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
